import { useState, type FormEvent } from 'react'
import { Navigate } from 'react-router'
import { useSesion } from '../auth/useSesion'
import {
  IconoAgendamiento,
  IconoAvisos,
  IconoCandado,
  IconoOcultar,
  IconoRuta,
  IconoVer,
} from '../componentes/iconos'
import { LogisticaIconoTema } from '../componentes/marca/LogisticaIcono'
import { ConmutadorTema } from '../tema/ConmutadorTema'

/**
 * Pantalla de acceso con el mismo diseno que Trazabis Contable y Proyectos
 * (Trazabis DS): panel de marca a la izquierda -ondas de linea muy suaves, la
 * marca del aplicativo, una ilustracion de linea (un recorrido de reparto con
 * sus paradas), titular y chips- y formulario a la derecha con el conmutador
 * de tema. Las clases `lg-login-*` viven en `index.css`, sobre tokens `--tz-*`.
 *
 * Es un `<form>` de verdad, no un boton suelto: asi se envia con Enter desde
 * cualquiera de los dos campos.
 *
 * **En movil el panel de marca desaparece** y queda el formulario con la marca
 * encima. La entrada del formulario es lo unico animado y se apaga con
 * `prefers-reduced-motion`.
 */

// No hay endpoint de recuperacion: las contrasenas las rotan los
// administradores a mano. Asi que el enlace no lleva a una pantalla que no
// existe, sino que explica a quien hay que pedirselo. Si esta puesto el correo
// de soporte, ademas abre el cliente de correo con el asunto escrito.
const CORREO_SOPORTE: string | undefined = import.meta.env.VITE_CORREO_SOPORTE

/** Correo con forma de correo: se avisa en el navegador antes de molestar al servidor. */
const CORREO_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/** Marca: icono del aplicativo (claro/oscuro) + nombre + «Panel de operacion». */
function Marca() {
  return (
    <div className="lg-login-marca-row">
      <LogisticaIconoTema size={44} />
      <div>
        <p className="lg-login-marca-nombre">Logística</p>
        <p className="lg-login-marca-sub">Panel de operación</p>
      </div>
    </div>
  )
}

/** Ilustracion de linea: un recorrido de reparto con sus paradas. Decorativa. */
function Ilustracion() {
  return (
    <svg className="lg-login-plano" width="340" height="206" viewBox="0 0 340 206" fill="none" aria-hidden="true">
      <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* hoja de ruta con esquina doblada */}
        <path d="M40 14h132l28 28v118H40z" />
        <path d="M172 14v28h28" />
        <path d="M58 40h62" strokeWidth="5" opacity=".55" />
        {/* recorrido con paradas */}
        <path d="M62 140 C 80 110, 96 120, 110 96 S 140 70, 160 82" strokeDasharray="4 5" />
        <circle cx="62" cy="140" r="6" />
        <circle cx="110" cy="96" r="6" />
        <circle cx="160" cy="82" r="6" opacity=".55" />
        <path d="M58 66h40" strokeWidth="7" opacity=".35" />
        {/* parada confirmada */}
        <circle cx="214" cy="86" r="13" />
        <path d="M208 86l4.5 4.5 8-8.5" />
        {/* cotas */}
        <path d="M40 182h160M40 176v12M200 176v12" strokeDasharray="4 5" />
        <path d="M232 14v146M226 14h12M226 160h12" strokeDasharray="4 5" />
      </g>
      <text x="120" y="198" textAnchor="middle" className="lg-login-cota">Rutas r1 a r5</text>
      <text x="246" y="92" className="lg-login-cota">Parada 3 de 8</text>
    </svg>
  )
}

/** Fondo de ondas de linea muy suaves. Decorativo. */
function Ondas() {
  const lineas = Array.from({ length: 11 }, (_, i) => 250 + i * 62)
  return (
    <svg className="lg-login-ondas" viewBox="0 0 900 1000" preserveAspectRatio="none" aria-hidden="true">
      {lineas.map((y) => (
        <path
          key={y}
          d={`M-20 ${y + 40} C 180 ${y - 30}, 330 ${y + 120}, 520 ${y + 30} S 820 ${y - 70}, 940 ${y - 20}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
        />
      ))}
    </svg>
  )
}

export function Login() {
  const { sesion, cargando, entrar } = useSesion()
  const [correo, setCorreo] = useState('')
  const [correoTocado, setCorreoTocado] = useState(false)
  const [contrasena, setContrasena] = useState('')
  const [verContrasena, setVerContrasena] = useState(false)
  const [verAyuda, setVerAyuda] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  // Solo se avisa del correo al salir del campo y con algo escrito: un campo
  // vacio sin estrenar no es un error.
  const correoInvalido = correoTocado && correo.length > 0 && !CORREO_RE.test(correo)
  const puedeEnviar = !enviando && CORREO_RE.test(correo) && contrasena.length > 0

  // Ya identificado: no tiene sentido enseñar el formulario.
  if (!cargando && sesion) return <Navigate to="/logistica" replace />

  async function alEnviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    setError(null)
    setEnviando(true)
    try {
      await entrar(correo, contrasena)
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se ha podido iniciar sesión')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="lg-login">
      {/* ── Panel de marca ─────────────────────────────────────────────── */}
      <aside className="lg-login-brand">
        <Ondas />
        <div className="lg-login-brand-top">
          <Marca />
          <Ilustracion />
        </div>

        <div className="lg-login-brand-mid">
          <p className="lg-login-eyebrow">Rutas · Clientes · Avisos · Agendamiento</p>
          <h2 className="lg-login-title">Toda la operación de tu equipo, en un solo lugar.</h2>
          <p className="lg-login-lead">
            Logística, conversaciones, avisos y agendamiento sincronizados en tiempo real, con
            acceso seguro desde cualquier dispositivo.
          </p>
          <ul className="lg-login-chips" aria-label="Qué hace Logística">
            <li className="lg-login-chip">
              <IconoRuta aria-hidden="true" />
              Rutas de reparto
            </li>
            <li className="lg-login-chip">
              <IconoAvisos aria-hidden="true" />
              Avisos a clientes
            </li>
            <li className="lg-login-chip">
              <IconoAgendamiento aria-hidden="true" />
              Agendamiento
            </li>
          </ul>
        </div>

        {/* La autoria, al pie: dice quien lo hace sin competir con la marca. */}
        <p className="lg-login-credit">Desarrollado por Servicios Digitales</p>
      </aside>

      {/* ── Formulario ─────────────────────────────────────────────────── */}
      <main className="lg-login-form">
        <div className="lg-login-theme">
          <ConmutadorTema rotuloVisible={false} />
        </div>

        <div className="lg-login-box">
          {/* Solo en movil, donde el panel de marca no esta. */}
          <div className="lg-login-marca-movil">
            <Marca />
          </div>

          <h1 className="lg-login-h1">Inicia sesión</h1>
          <p className="lg-login-sub">Bienvenido de nuevo. Introduce tus datos para continuar.</p>

          <form onSubmit={alEnviar} className="lg-login-fields">
            <div className="lg-login-field">
              <label htmlFor="correo">Correo electrónico</label>
              <input
                id="correo"
                name="correo"
                type="email"
                autoComplete="username"
                required
                autoFocus
                placeholder="nombre@empresa.com"
                value={correo}
                onChange={(evento) => setCorreo(evento.target.value)}
                onBlur={() => setCorreoTocado(true)}
                aria-invalid={correoInvalido || undefined}
                aria-describedby={correoInvalido ? 'correo-error' : undefined}
                className="lg-login-input"
              />
              {correoInvalido ? (
                <p id="correo-error" className="lg-login-campo-error">
                  Escribe un correo válido, por ejemplo nombre@empresa.com
                </p>
              ) : null}
            </div>

            <div className="lg-login-field">
              <div className="lg-login-field-row">
                <label htmlFor="contrasena">Contraseña</label>
                <button
                  type="button"
                  onClick={() => setVerAyuda((abierta) => !abierta)}
                  aria-expanded={verAyuda}
                  aria-controls="ayuda-contrasena"
                  className="lg-login-link"
                >
                  He olvidado mi contraseña
                </button>
              </div>
              {/* El ojo va dentro del campo: el hueco de la derecha se reserva
                  con padding y el boton se posiciona encima. */}
              <div className="lg-login-pass">
                <input
                  id="contrasena"
                  name="contrasena"
                  type={verContrasena ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  placeholder="Tu contraseña"
                  value={contrasena}
                  onChange={(evento) => setContrasena(evento.target.value)}
                  className="lg-login-input"
                />
                <button
                  type="button"
                  onClick={() => setVerContrasena((visible) => !visible)}
                  aria-label={verContrasena ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  aria-pressed={verContrasena}
                  className="lg-login-pass-btn"
                >
                  {verContrasena ? <IconoOcultar aria-hidden="true" /> : <IconoVer aria-hidden="true" />}
                </button>
              </div>

              {verAyuda ? (
                <p id="ayuda-contrasena" className="lg-login-ayuda">
                  El panel no envía correos de recuperación: las contraseñas las restablece un
                  administrador.{' '}
                  {CORREO_SOPORTE ? (
                    <a
                      href={`mailto:${CORREO_SOPORTE}?subject=${encodeURIComponent('Restablecer contraseña del panel')}`}
                    >
                      Escribe a {CORREO_SOPORTE}
                    </a>
                  ) : (
                    <span>Pídeselo al administrador del panel</span>
                  )}{' '}
                  indicando tu correo y te darán una nueva.
                </p>
              ) : null}
            </div>

            {error ? (
              <p role="alert" className="lg-login-error">
                {error}
              </p>
            ) : null}

            <button type="submit" disabled={!puedeEnviar} className="lg-login-submit">
              {enviando ? 'Entrando...' : 'Iniciar sesión'}
            </button>
          </form>

          <div className="lg-login-nota">
            <p>
              <IconoCandado aria-hidden="true" />
              Conexión cifrada
            </p>
            <p>El acceso lo da el administrador del panel.</p>
            {/* En movil el panel de marca no esta: la autoria se ve aqui. */}
            <p className="lg-login-credit-movil">Desarrollado por Servicios Digitales</p>
          </div>
        </div>
      </main>
    </div>
  )
}
